/**
 * Password generator — pure logic.
 *
 * The character draw takes an injected `randomIndex(maxExclusive)` so it is
 * deterministic and unit-testable; the component supplies a CSPRNG-backed
 * implementation (`crypto.getRandomValues` with rejection sampling).
 */

export interface PasswordOptions {
  length: number;
  upper: boolean;
  lower: boolean;
  digits: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
}

export const CHAR_SETS = {
  upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  lower: "abcdefghijklmnopqrstuvwxyz",
  digits: "0123456789",
  symbols: "!@#$%^&*()_+-=[]{}|;:,.<>?",
} as const;

/** Characters that look alike and are confusing in many fonts. */
export const AMBIGUOUS = "Il1O0o";

export function buildCharset(opts: PasswordOptions): string {
  let chars = "";
  if (opts.upper) chars += CHAR_SETS.upper;
  if (opts.lower) chars += CHAR_SETS.lower;
  if (opts.digits) chars += CHAR_SETS.digits;
  if (opts.symbols) chars += CHAR_SETS.symbols;
  if (opts.excludeAmbiguous) {
    chars = [...chars].filter((c) => !AMBIGUOUS.includes(c)).join("");
  }
  return chars;
}

/** Shannon entropy of a random password: length × log2(alphabet size). */
export function entropyBits(length: number, charsetSize: number): number {
  if (length <= 0 || charsetSize <= 1) return 0;
  return length * Math.log2(charsetSize);
}

export type Strength = "weak" | "fair" | "strong" | "very-strong";

export function strength(bits: number): Strength {
  if (bits < 40) return "weak";
  if (bits < 60) return "fair";
  if (bits < 80) return "strong";
  return "very-strong";
}

/**
 * Build a password of `length` from `charset`, drawing each character via
 * `randomIndex(maxExclusive)`. Returns "" if no charset or non-positive length.
 */
export function generate(
  length: number,
  charset: string,
  randomIndex: (maxExclusive: number) => number,
): string {
  if (!charset || length <= 0) return "";
  let out = "";
  for (let i = 0; i < length; i++) {
    out += charset[randomIndex(charset.length)];
  }
  return out;
}
